<template>
  <div class="card dashboard-widget incoming-message-widget">
    <div class="card-header">MESSAGES</div>
    <div class="card-body">
      <div class="row">
        <div class="col-sm-12 col-xl-8">
          <div class="chart-container">
            <GChart
              class="g-chart"
              type="LineChart"
              :data="incomingMessageHistoryChartData"
              :options="chartOptions"
            ></GChart>
          </div>
        </div>
        <div class="col-sm-12 col-xl-4">
          <div class="row incoming-block-stat">
            <div class="col-12 col-sm-4 col-xl-12">
              <div class="stat-block">
                <div class="stat-label">INCOMING MESSAGES</div>
                <div class="stat-value">
                  <span>{{incomingMessageStat.totalIncomingMessage | number}}</span>
                  <span class="changes positive" v-if="incomingMessageStat.incomingMessageTrend>0">
                    <font-awesome-icon class="changes-icon" icon="chevron-up"></font-awesome-icon>
                    <span class="changes-value">
                      (+{{incomingMessageStat.incomingMessageTrend}}%)
                    </span>
                  </span>
                  <span
                    class="changes negative"
                    v-else-if="incomingMessageStat.incomingMessageTrend<0"
                  >
                    <font-awesome-icon class="changes-icon" icon="chevron-down"></font-awesome-icon>
                    <span class="changes-value">
                      ({{incomingMessageStat.incomingMessageTrend}}%)
                    </span>
                  </span>
                </div>
                <div class="list-item">
                  <font-awesome-icon class="icon" icon="dot-circle"></font-awesome-icon>
                  <span>avg {{incomingMessageStat.avgMessagePerUser}} messages/user</span>
                </div>
              </div>
            </div>
            <div class="col-12 col-sm-4 col-xl-12">
              <div class="stat-block">
                <div class="stat-label">CONVERSATION</div>
                <div class="stat-value">
                  <span>{{conversationStat.totalConversation | number}}</span>
                  <span class="changes positive" v-if="conversationStat.conversationTrend>0">
                    <font-awesome-icon class="changes-icon" icon="chevron-up"></font-awesome-icon>
                    <span class="changes-value">(+{{conversationStat.conversationTrend}}%)</span>
                  </span>
                  <span class="changes negative" v-else-if="conversationStat.conversationTrend<0">
                    <font-awesome-icon class="changes-icon" icon="chevron-down"></font-awesome-icon>
                    <span class="changes-value">({{conversationStat.conversationTrend}}%)</span>
                  </span>
                </div>
                <div class="list-item">
                  <font-awesome-icon class="icon" icon="dot-circle"></font-awesome-icon>
                  <span>avg {{conversationStat.avgMessagePerConversation}} messages/user</span>
                </div>
                <div class="list-item">
                  <font-awesome-icon class="icon" icon="dot-circle"></font-awesome-icon>
                  <span>completed {{conversationStat.completeConversation}} conversations</span>
                </div>
              </div>
            </div>
            <div class="col-12 col-sm-4 col-xl-12">
              <div class="stat-block">
                <div class="stat-label">CONVERSATION LENGTH</div>
                <div class="stat-value">
                  <span>{{convertTime(conversationStat.conversationLength)}}</span>
                  <span class="changes positive" v-if="conversationStat.conversationLengthTrend>0">
                    <font-awesome-icon class="changes-icon" icon="chevron-up"></font-awesome-icon>
                    <span class="changes-value">
                      (+{{conversationStat.conversationLengthTrend}}%)
                    </span>
                  </span>
                  <span
                    class="changes negative"
                    v-else-if="conversationStat.conversationLengthTrend<0"
                  >
                    <font-awesome-icon class="changes-icon" icon="chevron-down"></font-awesome-icon>
                    <span class="changes-value">
                      (-{{conversationStat.conversationLengthTrend}}%)
                    </span>
                  </span>
                </div>
                <div class="stat-value-unit">
                  {{convertUnit(conversationStat.conversationLength)}}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { GChart } from 'vue-google-charts';
import moment from 'moment';

export default {
  name: 'incoming-message-widget',
  components: {
    GChart,
  },
  props: {
    criteria: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      chartOptions: {
        title: 'Incoming messages',
        legend: { position: 'in' },
        chartArea: {
          left: 50, top: 20, right: 20, bottom: 80, width: '50%', height: '100%',
        },
        hAxis: { showTextEvery: 4 },
      },
    };
  },
  watch: {
    criteria: {
      handler: 'init',
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      loadIncomingMessageData: 'dashboard/loadIncomingMessageData',
    }),
    async init() {
      await this.loadIncomingMessageData({
        criteria: this.criteria,
      });
    },
    convertTime(ms) {
      const input = moment.duration(ms);
      if (input.as('hours') >= 1) return input.as('hours').toFixed(2);
      if (input.as('minutes') >= 1) return input.as('minutes').toFixed(2);
      return input.as('seconds');
    },
    convertUnit(ms) {
      const input = moment.duration(ms);
      if (input.as('hours') >= 1) return 'hour(s)';
      if (input.as('minutes') >= 1) return 'minute(s)';
      if (input.as('seconds') >= 1) return 'second(s)';
      return 'second';
    },
  },
  computed: {
    ...mapGetters({
      incomingMessageData: 'dashboard/incomingMessageData',
      incomingMessageStat: 'dashboard/incomingMessageStat',
      conversationStat: 'dashboard/conversationStat',
      incomingMessageHistory: 'dashboard/incomingMessageHistory',
    }),
    incomingMessageHistoryChartData() {
      const { criteria } = this;
      let header = ['Date', 'Messages'];
      let dateFormat = '';
      if (criteria) {
        if (criteria.time.granularity === 'month') {
          header = ['Month', 'Messages'];
          dateFormat = 'M/YY';
        } else if (criteria.time.granularity === 'day') {
          header = ['Date', 'Messages'];
          dateFormat = 'D/M/YY';
        } else if (criteria.time.granularity === 'hour') {
          header = ['Hour', 'Messages'];
          dateFormat = 'HH:mm';
        }
      }
      if (this.incomingMessageHistory) {
        const dataList = this.incomingMessageHistory.map((obj) => [
          moment(obj.date).format(dateFormat), obj.count,
        ]);
        return [header, ...dataList];
      }
      return [header, ['No data', 0]];
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
